import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Typography, Paper, Container, CircularProgress } from '@mui/material';
import { Close as CloseIcon, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setUserData } from '../redux/actions/userActions';

const Test = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = useSelector(state => state.user);
  const testUrl = (userData?.testDone === true) ? (userData?.solutionUrl) : userData?.testUrl;


  useEffect(() => {
    // Simulate loading the test iframe
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Add event listener for messages from the iframe
    const handleMessage = (event) => {
      // Verify the origin of the message for security

      if (event.data && event.data.type === "result_screen") {
        console.log("Received user data from iframe:", event.data.data);
        if (isNaN(event?.data?.data?.percentage)) {
          event.data.data.percentage = 0;
        }  
        // Update Redux with test completion status and percentage
        // Preserve existing user data and only update specific fields
        dispatch(setUserData({
          ...userData, // Keep existing user data
          testCompleted: true,
          percentage: event.data.data.percentage || 0,
          correct_count: event.data.data.correct_count || 0,
          incorrect_count: event.data.data.incorrect_count || 0,
          // Add any other specific fields from event.data.data that need to be updated
          ...(event.data.data.percentage ? { percentage: event.data.data.percentage } : {})
        }));

        // Navigate to the test completed page
        navigate('/test-completed');
      }

      if (event.data && event.data.type === "backPressed") {
        console.log("Received user data from iframe:", event.data.data);
        dispatch(setUserData({
          ...userData,
          testState: 'resume'
        }));

        // Navigate to the test completed page
        navigate('/dashboard');
      }
      console.log('#########################', event.data);
      if (event.data && event.data.type === "fullScreen") {
        console.log("Received user data from iframe:", event.data.data);
        // Navigate to the test completed page
        toggleFullscreen()
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [navigate, dispatch]);

  // Redirect if no testUrl is available
  useEffect(() => {
    if (!testUrl) {
      navigate('/dashboard');
    }
  }, [testUrl, navigate]);

  // Fullscreen change event handler
  useEffect(() => {
    const fullscreenChangeHandler = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener('fullscreenchange', fullscreenChangeHandler);
    document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('mozfullscreenchange', fullscreenChangeHandler);
    document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);

    return () => {
      document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('webkitfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('mozfullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('MSFullscreenChange', fullscreenChangeHandler);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) { /* Safari */
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.mozRequestFullScreen) { /* Firefox */
        containerRef.current.mozRequestFullScreen();
      } else if (containerRef.current.msRequestFullscreen) { /* IE/Edge */
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) { /* Safari */
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) { /* Firefox */
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) { /* IE/Edge */
        document.msExitFullscreen();
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Loading your test...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we prepare your assessment
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 4,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Test
          </Typography>
          <Typography variant="body1" paragraph>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please try refreshing the page or contact support if the problem persists.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column'
    }}>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ flex: 1, py: 0 }} ref={containerRef}>
        <Paper
          elevation={2}
          sx={{
            height: 'calc(100vh - 48px)', // Adjust height to account for header height
            overflow: 'hidden',
            margin: '10px 0'
            // borderRadius: 2
          }}
        >
          <iframe

            ref={iframeRef}
            src={testUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: 'white',
            }}
            title="Scholarship Test"
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default Test;
